// The office app's device copies (IndexedDB): customers and jobs through the change index
// (changeFeed.js), closed jobs/inventory/mooring tags through deviceCopy.js, and the smaller roots
// (rentals, map, op codes, vendors, receiving, POs) by their lastUpdated mark. The Realtime Database
// bills for downloads, so every sync on sign-in lives here (docs/change-index.md).
// Split out of app.js (docs/split-plan.md, session 1); fetchCustomerDataAndGenerateTable comes in
// through setDeviceSyncDeps() (see estimates.js for why it doesn't import app.js back).
import { catchUp } from './changeFeed';
import { deviceCopy } from './deviceCopy';
import { database, isFirebaseConnected } from './initFirebase';
import { pruneDeletedJobs } from './jobCache';
import { fetchFirebaseLastUpdated, fetchIndexedDBLastUpdated, getDB, notifyLocalDataChanged } from './utilities';
import { get, orderByChild, query, ref, startAt } from 'firebase/database';

let shell = null;
export function setDeviceSyncDeps(deps) { shell = deps; }

let customersCatchingUp = null;
export async function getCustomerOpen() {
  try {
    // ✅ Load from IndexedDB immediately for faster offline-first UX
    shell.fetchCustomerDataAndGenerateTable();

    // ⏳ In the background, catch up on what changed since this device last did (changeFeed.js):
    // only those customers are fetched; everyone only on a device's first time. Start-up paths
    // that call this at the same moment share one catch-up.
    if (isFirebaseConnected()) {
      if (!customersCatchingUp) {
        customersCatchingUp = catchUp({ root: 'customers', store: 'customerData', full: downloadAllCustomers })
          .finally(() => { customersCatchingUp = null; });
      }
      const changed = await customersCatchingUp;
      if (changed?.length) {
        notifyLocalDataChanged('customerData');
        shell.fetchCustomerDataAndGenerateTable(); // 🔁 Refresh UI after update
      }
    }

  } catch (error) {
    console.error('Error in getCustomerOpen:', error);
  }
}


async function downloadAllCustomers() {
  const db = await getDB();
  const snapshot = await get(ref(database, '/customers'));
  await new Promise((resolve, reject) => {
    const transaction = db.transaction('customerData', 'readwrite');
    const store = transaction.objectStore('customerData');
    const onServer = new Set();
    snapshot.forEach((childSnapshot) => {
      const val = childSnapshot.val();
      if (childSnapshot.key === 'lastUpdated' || !val || typeof val !== 'object') return;
      onServer.add(childSnapshot.key);
      store.put({ id: childSnapshot.key, ...val });
    });
    // Customers deleted while this device wasn't listening.
    const keys = store.getAllKeys();
    keys.onsuccess = () => keys.result.forEach(id => { if (!onServer.has(id)) store.delete(id); });
    transaction.oncomplete = () => {
      console.log('IndexedDB updated with new Firebase data.');
      resolve();
    };
    transaction.onerror = (event) => reject(event.target.error);
  });
  notifyLocalDataChanged('customerData');
  shell.fetchCustomerDataAndGenerateTable();
}

// The device's copy of /mooringDatabase/tags through the change index (deviceCopy.js). It
// shared its "last updated" mark with the rentals sync below, so one could skip the other.
export async function getMooringDatabaseOpen() {
  try {
    await deviceCopy('mooringDatabase/tags').sync();
  } catch (error) {
    console.error('Error in getMooringDatabaseOpen:', error);
  }
}

export async function getRentalDatabaseOpen() {
  try {
      const db = await getDB();

    if (isFirebaseConnected()) {
      const firebaseTimestamp = await fetchFirebaseLastUpdated('mooringDatabase');
      const indexedDBTimestamp = await fetchIndexedDBLastUpdated(db, 'mooringDatabaseLastUpdated');

      if (!indexedDBTimestamp || indexedDBTimestamp < firebaseTimestamp) {
        const mooringRef = ref(database, '/mooringDatabase/rentals');
        const snapshot = await get(mooringRef);

        const transaction = db.transaction(['rentalDatabase', 'metadata'], 'readwrite');
        const store = transaction.objectStore('rentalDatabase');
        const metadataStore = transaction.objectStore('metadata');

        const indexedDBMooringIDs = await new Promise((resolve, reject) => {
          const request = store.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const firebaseMooringIDs = [];

        snapshot.forEach((childSnapshot) => {
          const childKey = childSnapshot.key;
          const childData = childSnapshot.val();

          firebaseMooringIDs.push(childKey);

          store.put({ id: childKey, ...childData });
        });

        // Identify and remove deleted mooring from IndexedDB
        const deletedMooringIDs = indexedDBMooringIDs.filter(id => !firebaseMooringIDs.includes(id));
        deletedMooringIDs.forEach(id => store.delete(id));


        transaction.oncomplete = () => {
          console.log('Rental database has been updated in IndexedDB.');
        };

        transaction.onerror = (event) => {
          console.error('Transaction error:', event.target.errorCode);
        };
      }
    }
  } catch (error) {
    console.error('Error in getRentalDatabaseOpen:', error);
  }
}

// The device's copy of /data (IndexedDB jobData): brought up to date through the change index,
// fetching only the jobs written or removed since this device last caught up (changeFeed.js).
// The whole 2-year download is only for a device's first time, or when a lot changed meanwhile.
// Several start-up paths call this at once: they share one catch-up.
let jobsCatchingUp = null;
let jobsPruned = false;
export function getJobsOpen() {
  if (!isFirebaseConnected()) return Promise.resolve();
  if (!jobsCatchingUp) {
    jobsCatchingUp = catchUpJobs().finally(() => { jobsCatchingUp = null; });
  }
  return jobsCatchingUp;
}

async function catchUpJobs() {
  try {
    // Once a load, a keys-only read drops jobs gone from /data that this device still has — the
    // change index lists removals too, so this is a safety net (jobCache.js).
    if (!jobsPruned) {
      jobsPruned = true;
      const gone = await pruneDeletedJobs();
      if (gone.length) {
        console.log(`Removed ${gone.length} deleted job(s) from this device.`);
        notifyLocalDataChanged('jobData');
      }
    }
    const changed = await catchUp({ root: 'data', store: 'jobData', full: downloadAllJobs });
    if (changed?.length) {
      console.log(`Job data: ${changed.length} change(s) since last time.`);
      notifyLocalDataChanged('jobData');
    }
  } catch (err) {
    console.error('Error in getJobsOpen:', err);
  }
}

async function downloadAllJobs() {
  // Only jobs from the last 2 years. Older jobs already in IDB are left intact.
  const cutoff = new Date();
  cutoff.setFullYear(cutoff.getFullYear() - 2);
  const cutoffStr = cutoff.toISOString().split('T')[0];

  const db = await getDB();
  const jobsQuery = query(ref(database, '/data'), orderByChild('Start_Date'), startAt(cutoffStr));
  const dataSnap = await get(jobsQuery);
  const records = Object.entries(dataSnap.val() || {})
    .filter(([id, details]) => id !== 'lastUpdated' && details && typeof details === 'object')
    .map(([id, details]) => ({ id, ...details }));

  await new Promise((resolve, reject) => {
    const tx = db.transaction('jobData', 'readwrite');
    const jobStore = tx.objectStore('jobData');
    // Upsert only: we fetched a window, so deleting by key would remove pre-cutoff jobs.
    for (const j of records) jobStore.put(j);
    tx.oncomplete = () => {
      console.log('Job data synced.');
      resolve();
    };
    tx.onerror = (e) => reject(e.target?.error || e);
    tx.onabort = (e) => reject(e.target?.error || e);
  });
  notifyLocalDataChanged('jobData');
}




// The device's copy of /closedJobs (IndexedDB closedJobs) through the change index
// (deviceCopy.js): only the closed jobs written since this device last caught up. It used to
// download all of /closedJobs (about 5 MB) whenever any job had closed since the last load.
export async function getJobsClosed() {
  try {
    await deviceCopy('closedJobs').sync();
  } catch (error) {
    console.error('Error in getJobsClosed:', error);
  }
}

export async function getMapData() {
  try {
const db = await getDB();

    if (navigator.onLine) {
      // Fetch timestamps
      const firebaseTimestampBoats = await fetchFirebaseLastUpdated('maps/boats');
      const indexedDBTimestampBoats = await fetchIndexedDBLastUpdated(db, 'boatDataLastUpdated');

      const firebaseTimestampMoorings = await fetchFirebaseLastUpdated('maps/moorings');
      const indexedDBTimestampMoorings = await fetchIndexedDBLastUpdated(db, 'mooringDataLastUpdated');

      // Update boats data if necessary
      if (!indexedDBTimestampBoats || indexedDBTimestampBoats < firebaseTimestampBoats) {
        const boatsRef = ref(database, '/maps/boats');
        const snapshotBoats = await get(boatsRef);

        const transactionBoats = db.transaction(['mapDataBoats', 'metadata'], 'readwrite');
        const boatStore = transactionBoats.objectStore('mapDataBoats');
        const metadataStore = transactionBoats.objectStore('metadata');

        const indexedDBBoatIDs = await new Promise((resolve, reject) => {
          const request = boatStore.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const firebaseBoatIDs = [];

        snapshotBoats.forEach((childSnapshot) => {
          const childKey = childSnapshot.key;
          const childData = childSnapshot.val();

          firebaseBoatIDs.push(childKey);

          boatStore.put({ id: childKey, ...childData });
        });

        const deletedBoatIDs = indexedDBBoatIDs.filter(id => !firebaseBoatIDs.includes(id));
        deletedBoatIDs.forEach(id => boatStore.delete(id));

        metadataStore.put({ key: 'boatDataLastUpdated', timestamp: firebaseTimestampBoats });

        transactionBoats.oncomplete = () => {
          console.log('All boats have been updated in IndexedDB.');
        };

        transactionBoats.onerror = (event) => {
          console.error('Transaction error:', event.target.errorCode);
        };
      }
      // Update moorings data if necessary
      if (!indexedDBTimestampMoorings || indexedDBTimestampMoorings < firebaseTimestampMoorings) {
        const mooringsRef = ref(database, '/maps/moorings');
        const snapshotMoorings = await get(mooringsRef);

        const transactionMoorings = db.transaction(['mapDataMoorings', 'metadata'], 'readwrite');
        const mooringStore = transactionMoorings.objectStore('mapDataMoorings');
        const metadataStore = transactionMoorings.objectStore('metadata');

        const indexedDBMooringIDs = await new Promise((resolve, reject) => {
          const request = mooringStore.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const firebaseMooringIDs = [];

        snapshotMoorings.forEach((childSnapshot) => {
          const childKey = childSnapshot.key;
          const childData = childSnapshot.val();

          firebaseMooringIDs.push(childKey);

          mooringStore.put({ id: childKey, ...childData });
        });

        const deletedMooringIDs = indexedDBMooringIDs.filter(id => !firebaseMooringIDs.includes(id));
        deletedMooringIDs.forEach(id => mooringStore.delete(id));

        metadataStore.put({ key: 'mooringDataLastUpdated', timestamp: firebaseTimestampMoorings });

        transactionMoorings.oncomplete = () => {
          console.log('All moorings have been updated in IndexedDB.');
        };

        transactionMoorings.onerror = (event) => {
          console.error('Transaction error:', event.target.errorCode);
        };
      }
    }
  } catch (error) {
    console.error('Error in getMapData:', error);
  }
}

export async function getOpCodes() {
  try {
const db = await getDB();

    if (navigator.onLine) {
      const firebaseTimestamp = await fetchFirebaseLastUpdated('opcodes');
      const indexedDBTimestamp = await fetchIndexedDBLastUpdated(db, 'opCodeDataLastUpdated');

      if (!indexedDBTimestamp || indexedDBTimestamp < firebaseTimestamp) {
        const opCodesRef = ref(database, '/opcodes');
        const snapshot = await get(opCodesRef);
        const opCodes = snapshot.val();

        const transaction = db.transaction(['opCodeData', 'metadata'], 'readwrite');
        const store = transaction.objectStore('opCodeData');
        const metadataStore = transaction.objectStore('metadata');

        const indexedDBOpCodeIDs = await new Promise((resolve, reject) => {
          const request = store.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const firebaseOpCodeIDs = Object.keys(opCodes);

        Object.entries(opCodes).forEach(([key, value]) => {
          store.put({ id: key, ...value });
        });

        const deletedOpCodeIDs = indexedDBOpCodeIDs.filter(id => !firebaseOpCodeIDs.includes(id));
        deletedOpCodeIDs.forEach(id => store.delete(id));

        metadataStore.put({ key: 'opCodeDataLastUpdated', timestamp: firebaseTimestamp });

        transaction.oncomplete = () => {
          console.log('All op codes have been updated in IndexedDB.');
        };

        transaction.onerror = (event) => {
          console.error('Transaction error:', event.target.errorCode);
        };
      }
    }
  } catch (error) {
    console.error('Error synchronizing op codes:', error);
  }
}

// The device's copy of /inventory through the change index (deviceCopy.js): only the parts
// changed since this device last caught up. It used to download every part whenever any
// part had changed since the last load.
export async function getInventory() {
  try {
    await deviceCopy('inventory').sync();
  } catch (error) {
    console.error('Error in getInventory:', error);
  }
}

export async function getVendors() {
  try {
const db = await getDB();

    if (navigator.onLine) {
      const firebaseTimestamp = await fetchFirebaseLastUpdated('settings');
      const indexedDBTimestamp = await fetchIndexedDBLastUpdated(db, 'settingsLastUpdated');

      if (!indexedDBTimestamp || indexedDBTimestamp < firebaseTimestamp) {
        const opCodesRef = ref(database, '/settings/vendorList');
        const snapshot = await get(opCodesRef);
        const opCodes = snapshot.val();

        const transaction = db.transaction(['vendorData', 'metadata'], 'readwrite');
        const store = transaction.objectStore('vendorData');
        const metadataStore = transaction.objectStore('metadata');

        const indexedDBOpCodeIDs = await new Promise((resolve, reject) => {
          const request = store.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const firebaseOpCodeIDs = Object.keys(opCodes);
        console.log(opCodes)

        Object.entries(opCodes).forEach(([key, value]) => {
          store.put({ id: key, ...value });
        });

        const deletedOpCodeIDs = indexedDBOpCodeIDs.filter(id => !firebaseOpCodeIDs.includes(id));
        deletedOpCodeIDs.forEach(id => store.delete(id));

        metadataStore.put({ key: 'settingsLastUpdated', timestamp: firebaseTimestamp });

        transaction.oncomplete = () => {
          console.log('All settings has been updated in IndexedDB.');
        };

        transaction.onerror = (event) => {
          console.error('Transaction error:', event.target.errorCode);
        };
      }
    }
  } catch (error) {
    console.error('Error synchronizing settings:', error);
  }
}

export async function getReceiving() {
  try {
const db = await getDB();

    if (navigator.onLine) {
      const firebaseTimestamp = await fetchFirebaseLastUpdated('receiving');
      const indexedDBTimestamp = await fetchIndexedDBLastUpdated(db, 'receivingDataLastUpdated');

      if (!indexedDBTimestamp || indexedDBTimestamp < firebaseTimestamp) {
        const opCodesRef = ref(database, '/receiving');
        const snapshot = await get(opCodesRef);
        const opCodes = snapshot.val();

        const transaction = db.transaction(['receivingData', 'metadata'], 'readwrite');
        const store = transaction.objectStore('receivingData');
        const metadataStore = transaction.objectStore('metadata');

        const indexedDBOpCodeIDs = await new Promise((resolve, reject) => {
          const request = store.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const firebaseOpCodeIDs = Object.keys(opCodes);

        Object.entries(opCodes).forEach(([key, value]) => {
          store.put({ id: key, ...value });
        });

        const deletedOpCodeIDs = indexedDBOpCodeIDs.filter(id => !firebaseOpCodeIDs.includes(id));
        deletedOpCodeIDs.forEach(id => store.delete(id));

        metadataStore.put({ key: 'receivingDataLastUpdated', timestamp: firebaseTimestamp });

        transaction.oncomplete = () => {
          console.log('All receiving has been updated in IndexedDB.');
        };

        transaction.onerror = (event) => {
          console.error('Transaction error:', event.target.errorCode);
        };
      }
    }
  } catch (error) {
    console.error('Error synchronizing inventory:', error);
  }
}

export async function getInventoryPOs() {
  try {
const db = await getDB();

    if (navigator.onLine) {
      const firebaseTimestamp = await fetchFirebaseLastUpdated('inventoryPOs');
      const indexedDBTimestamp = await fetchIndexedDBLastUpdated(db, 'inventoryPOsLastUpdated');

      if (!indexedDBTimestamp || indexedDBTimestamp < firebaseTimestamp) {
        const posRef = ref(database, '/inventoryPOs');
        const snapshot = await get(posRef);
        const pos = snapshot.val() || {};

        const transaction = db.transaction(['inventoryPOs', 'metadata'], 'readwrite');
        const store = transaction.objectStore('inventoryPOs');
        const metadataStore = transaction.objectStore('metadata');

        const indexedDBPoIDs = await new Promise((resolve, reject) => {
          const request = store.getAllKeys();
          request.onsuccess = () => resolve(request.result);
          request.onerror = () => reject(request.error);
        });

        const firebasePoIDs = Object.keys(pos);

        Object.entries(pos).forEach(([key, value]) => {
          store.put({ id: key, ...value });
        });

        const deletedPoIDs = indexedDBPoIDs.filter(id => !firebasePoIDs.includes(id));
        deletedPoIDs.forEach(id => store.delete(id));

        metadataStore.put({ key: 'inventoryPOsLastUpdated', timestamp: firebaseTimestamp });

        transaction.oncomplete = () => {
          console.log('All inventory POs have been updated in IndexedDB.');
        };

        transaction.onerror = (event) => {
          console.error('Transaction error:', event.target.errorCode);
        };
      }
    }
  } catch (error) {
    console.error('Error synchronizing inventory POs:', error);
  }
}
